<script>
export default {
	name: 'FileUpload',
};
</script>

<script setup>
import yup from 'mh-yup';
import Tooltip from '~/components/Tooltip.vue';
import useFieldValidation from '~/logic/composables/useFieldValidation.js';
import useGenerateIdentifier from '~/logic/composables/useGenerateIdentifier.js';
import { i18nGlobal } from '~/logic/i18n.js';
import useFormAccordionStatus from '~/logic/composables/useFormAccordionStatus.js';

const props = defineProps({
	name: { type: String, required: true },
	ariaLabel: { type: String, default: '' },
	
	modelValue: { type: [String, Number, Array], default: null },
	label: { type: String, default: '' },
	desktopUploadButtonLabel: { type: String, default: '' },
	mobileUploadButtonLabel: { type: String, default: '' },

	isRequired: { type: Boolean, default: false },
	requiredErrorMsg: { type: String, default: i18nGlobal.t('This field is required') },
	isMhe: { type: Boolean, default: false },
	highlightField: { type: Boolean, default: false },
	maxNoAttachmentLimitLabel: { type: String, default: '' },
	maximumNoAttachmentLimit: { type: Number, default: 10 },
	maxFileSize: { type: Number, default: 5 },
	fileFormatSupportedLabel: { type: String, default: '' },
	supportedFileFormatsErrorMessage: { type: String, default: '' },
	listAllowedFileExtensions: { type: String, default: '' },
	fileTooLargeErrorMessage: { type: String, default: '' },
	isFileUploadDisabled: { type: Boolean, default: false },
	canSelectMultipleFile: { type: Boolean, default: true },
	lockFields: { type: Boolean, default: false },
	
	validation: {
		type: [Object, Function],
		default: null,
		// only accepts Yup object, or a function
		validator: (val) => ((val === null) || (val instanceof yup.BaseSchema) || (val instanceof Function)),
	},
});

const emit = defineEmits([
	'update:modelValue',
	'file-change',
	'file-validation-failed',
]);

const rootEl = ref(null);
const inputEl = ref(null);

const attachedFiles = ref([]);
const isMaxLimitFilesReached = ref(false);

const validation = computed(() => {
	const schema = yup.array().nullable();
	
	if ( !props.isRequired || attachedFiles.value.length !== 0) {
		return null;
	}
	
	return schema.required(props.requiredErrorMsg);
});

const {
	onInput,
	meta,
	errors,
	setTouched,
	isRequired,
	hasValidationError,
	internalValue,
	validate,
} = useFieldValidation({
	name: toRef(props, 'name'),
	validation: validation,
	modelValue: toRef(props, 'modelValue'),
});


const {
	errorMsgId,
	describedbyId,
	detailsId,
} = useGenerateIdentifier(toRef(props, 'name'));



// const isValidFileMimeType = (file, validMimeTypes) => {
// 	return validMimeTypes.some((mime) => {
// 		return !!(file.type.match(mime.trim()));
// 	});
// };

// const validateFile = (file) => {
// 	// check mime type
// 	isValidFileMimeType(file, ['image/png']);
	
// 	// check file size
	
	
// 	return {
// 		pass: true,
// 		failedReasons: [
// 			'MIME_TYPE_UNSUPPORTED',
// 			'FILE_SIZE_EXCEEDED',
// 		],
// 	};
// };

const isAttachedEachFileReachMaxLimit = computed(() => {
	isMaxLimitFilesReached.value = false;
	
	if (attachedFiles.value.length > parseInt(props.maximumNoAttachmentLimit)) {
		isMaxLimitFilesReached.value = true;
	}
	
	return attachedFiles.value.length > parseInt(props.maximumNoAttachmentLimit);
});

const attachFile = (file) => {
	if ( isMaxLimitFilesReached.value ) return;
	
	attachedFiles.value.push(fileValidation(file));
	
	if (isAttachedEachFileReachMaxLimit.value || !doesFilePassValidation(file)) attachedFiles.value.pop();
	
	// Pass attachedFiles to submit valid value;
	updateFileList();
};

const removeFile = (file) => {
	attachedFiles.value = attachedFiles.value.filter((item) => item.file.name !== file.file.name);
	
	if (attachedFiles.value.length <= props.maximumNoAttachmentLimit) {
		isMaxLimitFilesReached.value = false;
	}
	
	updateFileList();
};

const updateFileList = () => {
	
	if (attachedFiles.value.length === 0) {
		internalValue.value = null;
		return;
	}
	
	internalValue.value = attachedFiles.value
		.filter((attachedFile) => {
			return attachedFile.failedReasons.length === 0;
		})
		.map((attachedFile) => {
			return attachedFile.file;
		});
		
};

const doesFilePassValidation = (file) => {
	
	return isFileSizeValid(file) || isMimeFileValid(file);
};

const isMimeFileValid = (file) => {
	const textExt = props.listAllowedFileExtensions;
	const extensionList = textExt.split(',').map((v) => v.toLowerCase().trim());
	const fileExtension = file.name.match(/[^.]+$/)[0];
	const fileExtensionWithComa = file.name.match(/(?:\.[^.]+)?$/)[0];
	
	return extensionList.includes(fileExtension) || extensionList.includes(fileExtensionWithComa);
};

const isFileSizeValid = (file) => {
	const totalAttachedFileSize = attachedFiles.value.map((obj) => obj.file.size).reduce((total, num) => total + num, 0) + file.size;
	
	// if (totalAttachedFileSize > (props.maxFileSize * 1000000)) return false;
	// console.log('totalAttachedFileSize', totalAttachedFileSize);
	
	return totalAttachedFileSize < (props.maxFileSize * 1000000);
};

const fileValidation = (file) => {
	const currentFile = {
		file,
		pass: true,
		failedReasons: [],
	};
	
	// validate MIME type
	if (!isMimeFileValid(file)) {
		// debugger;
		currentFile.pass = false;
		currentFile.failedReasons.push('MIME_TYPE_UNSUPPORTED');
	}
	
	// validate file size
	if (!isFileSizeValid(file)) {
		// debugger
		currentFile.pass = false;
		currentFile.failedReasons.push('FILE_SIZE_EXCEEDED');
	}
	
	// debugger;
	return currentFile;
};

watch(attachedFiles, (newValue, oldValue) => {
	// emit();
});

const isDropZoneActive = ref(false);
let inactiveTimeout = null;

const onDrop = (event) => {
	if (attachedFiles.value.length > props.maximumNoAttachmentLimit) return;
	
	isDropZoneActive.value = false;
	
	// Reset max limit state.
	isMaxLimitFilesReached.value = false;
	
	// Check if files uploaded more than expected
	isMaxLimitFilesReached.value = event.dataTransfer.files.length > parseInt(props.maximumNoAttachmentLimit);


	for (let i = 0; i < event.dataTransfer.files.length; i++) {
		attachFile(event.dataTransfer.files[i]);
	}
	
	emit('file-change', event.dataTransfer.files);
};

const setDropZoneActive = () => {
	isDropZoneActive.value = true;
	clearTimeout(inactiveTimeout);
};

const setDropZoneInactive = () => {
	inactiveTimeout = setTimeout(() => {
		isDropZoneActive.value = false;
	}, 50);
};

const onFileChanged = (event) => {
	// Reset max limit state.
	isMaxLimitFilesReached.value = false;
	
	// Check if files uploaded more than expected
	isMaxLimitFilesReached.value = event.currentTarget.files.length > parseInt(props.maximumNoAttachmentLimit);

	for (let i = 0; i < event.currentTarget.files.length; i++) {
		attachFile(event.currentTarget.files[i]);
	}
	
};

const hasMaxCountReached = computed(() => {
	return attachedFiles.value.length > parseInt(props.maximumNoAttachmentLimit);
});

const hasMaxFileSizeReached = computed(() => {
	const totalAttachedFileSize = attachedFiles.value.map((obj) => obj.file.size).reduce((total, num) => total + num, 0);
	const maxInt = parseInt(props.maxFileSize * 1000000);
	const totalMaxFileSize = maxInt * props.maximumNoAttachmentLimit;
	
	return totalAttachedFileSize > totalMaxFileSize;
});

const checkFileExtension = (type, fileName) => {
	let extentionList = [];
	let currFileExtension = `${fileName.match(/(?:\.[^.]+)?$/)[0]}`;

	switch (type) {
		case 'image':
			extentionList = '.bmp, .gif, .jpeg, .jpg, .png, .svg, .tif, .tiff, .avif, .webp, .jxl'.split(',').map((v) => v.trim());
			return extentionList.includes(currFileExtension);
		case 'pdf':
			extentionList = '.pdf'.split(',').map((v) => v.trim());
			return extentionList.includes(currFileExtension);
		case 'other':
			extentionList = '.3g2, 3gp, .avi, .flv, .h264, .m4v, .mkv, .mov, .mp4, .mpg, .mpeg, .rm, .swf, .wmv, .odp, .pps, .ppt, .pptx, .ods, .xls, .xlsm, .xlsx, .doc, .docx, .odt, .dpf, .rtf, .tex, .txt, .wpd, .csv, .aif, .cda, .mid, .midi, .mp3, .mpa, .ogg, .wav, .wma, .wpl, .zip, .rar, .7z'.split(',').map((v) => v.trim());
			return extentionList.includes(currFileExtension);
	
		default:
			return false;
	}
	
};

const getFileExtensionName = (fileName) => {
	return fileName.match(/(?:\.[^.]+)?$/)[0];
};

const removeFileWithoutExtension = (fileName) => {
	return fileName.replace(/(?:\.[^.]+)?$/, '');
};

const listOfSupportedFileTypes = (fileTypeList) => {
	return fileTypeList.replaceAll('.', '').toUpperCase();
};


const hasError = computed(() => {
	return !!attachedFiles.value.map?.(({ failedReasons }) => failedReasons.map?.((reason) => reason).join(',') || '').join(',') || '';
});

useFormAccordionStatus(props.name, {
	required: props.isRequired,
	value: computed(() => attachedFiles.value?.length),
	error: hasError,
});

</script>



<template>
<div
	ref="rootEl"
	class="FileUpload"
	:class="{
		'is-dropzone-active': isDropZoneActive,
	}"
>
	<div
		class="inner-grand-container flex flex-col"
	>
		<div class="flex mb-4">
			<div class="label font-semibold">{{ props.label }}</div>
		</div>
		<div class="flex items-start" :class="{ 'disabled-item': props.isFileUploadDisabled }">
			<div
				class="inner-container flex flex-col flex-grow w-full"
				:class="{
					'error': errors.length !== 0 && !isMaxLimitFilesReached,
					'hightLight': props.highlightField,
					'mheHeight': props.isMhe,
				}"
				@dragenter.prevent="setDropZoneActive"
				@dragover.prevent="setDropZoneActive"
				@dragleave.prevent="setDropZoneInactive"
				@drop.prevent="onDrop"
			>
				<div class="inner-wrapper pl-5 pr-3 pt-8 pb-8">
					<input
						ref="inputEl"
						class="input file-upload-input"
						type="file"
						:multiple="canSelectMultipleFile"
						:required="isRequired"
						:aria-label="props.label"
						:aria-describedby="$slots['short-description'] ? describedbyId : null"
						:name="props.name"
						:disabled="props.isFileUploadDisabled"
						@change="onFileChanged"
					/>
					
					<icon-fas-circle-plus
						class="w-6 h-6 mb-2"
						:class="isMaxLimitFilesReached ? 'fill-current' : 'fill-primary-blue-base'"
						aria-hidden="true"
					/>
					
					<div class="text-center mb-4 text-primary-blue-base upload-button-wrapper">
						<span class="font-semibold hidden >md:block upload-button-label">{{ props.desktopUploadButtonLabel }}</span>
						<span class="font-semibold hidden md:block upload-button-label">{{ props.mobileUploadButtonLabel }}</span>
					</div>
					<div v-if="maxNoAttachmentLimitLabel" class="typo-body-text-2 mb-2 md:mb-4" :class="{ 'text-center': props.isMhe, 'md:text-center': !props.isMhe }">
						{{ props.maxNoAttachmentLimitLabel }}
						<span class="font-semibold inline-block">{{ props.maximumNoAttachmentLimit }} ({{ $t('Up to') }} {{ props.maxFileSize }}MB {{ $t('each') }})</span>
					</div>
					<div v-if="props.listAllowedFileExtensions" class="typo-body-text-2 md:text-center">
						{{ props.fileFormatSupportedLabel }}
						
						<!-- TODO: Map with dialog list file extension here -->
						<span class="font-semibold inline-block break-all">{{ listOfSupportedFileTypes(props.listAllowedFileExtensions) }}</span>
					</div>

				</div>
				
				<div v-if="isDropZoneActive" class="dropzone-hint flex items-center justify-center">
					<div class="rounded-xl bg-primary-blue-extralight px-5 py-4 m-10 flex items-center leading-none">
						<icon-far-file-import class="mr-3" />
						{{ $t('Drop file here to attach it') }}
					</div>
				</div>
			</div>

			<div v-if="$slots['tooltip-content']" class="tooltip-content-wrapper flex items-center ml-2">
				<Tooltip>
					<template #default>
						<icon-fas-circle-question class="fill-primary-blue-base" />
					</template>
					<!-- <template #mobile-title>
						<slot>{{ props.ariaLabel }}</slot>
					</template> -->
					<template #tooltip-content>
						<div :id="detailsId">
							<slot name="tooltip-content"></slot>
						</div>
					</template>
				</Tooltip>
			</div>
		</div>
		
		<div v-if="hasMaxFileSizeReached">
			<slot name="max-filesize-limit-msg"></slot>
		</div>
		
		<div v-if="isMaxLimitFilesReached">
			<div class="bg-primary-blue-extralight flex gap-3 justify-start px-5 py-4 rounded-xl mt-4">
				<icon-fas-circle-info class="fill-primary-blue-base text-base flex-none mt-0.5" />
				<slot name="max-limit-msg"></slot>
			</div>
		</div>
	</div>
	
	<hr class="my-5 h-[2px] bg-neutral-grey-light" />
	
	<div
		class="attached-files-container"
		:class="{ 'disabled-item': props.lockFields }"
	>
		<div
			v-for="(item, index) in attachedFiles"
			:key="index"
			class="attached-file-wrapper mb-3"
		>
			<div
				class="attached-file px-6 py-3"
				:class="{
					'error': item.failedReasons.length !== 0,
				}"
			>
				<icon-fas-file-image
					v-if="checkFileExtension('image', item.file.name)"
					class="mr-3"
					:class="{
						'fill-semantic-red-base': item.failedReasons.length !== 0,
					}"
				/>
				<icon-fas-file-pdf
					v-else-if="checkFileExtension('pdf', item.file.name)"
					class="mr-3"
					:class="{
						'fill-semantic-red-base': item.failedReasons.length !== 0,
					}"
				/>
				<icon-fas-file
					v-else-if="checkFileExtension('other', item.file.name)"
					class="mr-3"
					:class="{
						'fill-semantic-red-base': item.failedReasons.length !== 0,
					}"
				/>

				<!-- TODO: ERROR file icon require to integrate -->
				<icon-fas-file-exclamation
					v-else
					class="mr-3"
					:class="{
						'fill-semantic-red-base': item.failedReasons.length !== 0,
					}"
				/>
				
				<div
					class="inline-flex min-w-0 text-sm flex-1"
					:class="{
						'text-semantic-red-base': item.failedReasons.length !== 0,
					}"
				>
					<span
						class="text-ellipsis overflow-hidden"
					>{{ removeFileWithoutExtension(item.file.name) }}</span>
					<span class="">{{ getFileExtensionName(item.file.name) }}</span>
				</div>
				
				<!-- <span
					class="text-sm"
				>{{ item.file.name }}</span> -->
				<button v-button-press type="button" class="ml-auto btn-generic-b px-2" @click="removeFile(item)">
					<icon-fas-xmark v-if="item.failedReasons.length !== 0" class="fill-primary-blue-base" />
					<icon-fas-trash-can v-else class="" />
				</button>
			</div>
			
			<template
				v-for="(fileErrorName, errorIndex) in item.failedReasons"
				:key="errorIndex"
			>
				<p
					v-if="fileErrorName === 'FILE_SIZE_EXCEEDED'"
					v-html-sanitize="props.fileTooLargeErrorMessage"
					class="attached-file-error-msg mt-2 text-semantic-red-base text-sm"
				>
				</p>
				<p
					v-if="fileErrorName === 'MIME_TYPE_UNSUPPORTED'"
					v-html-sanitize="props.supportedFileFormatsErrorMessage"
					class="attached-file-error-msg mt-2 text-semantic-red-base text-sm"
				>
				</p>
			</template>
		</div>
	</div>
	
	<div
		:id="describedbyId"
		class="short-description-wrapper w-full text-sm empty:hidden"
	>
		<slot name="short-description"></slot>
	</div>
	<div
		:id="errorMsgId"
		class="error-msg-container leading-tight text-semantic-red-base text-sm w-full mt-1 empty:hidden"
	>
		<slot
			v-if="hasValidationError && meta.touched && !isMaxLimitFilesReached"
			name="error-messages"
			v-bind="{ meta, errors, internalValue }"
		><span class="">{{ errors[0] }}</span></slot>
	</div>
	
	
</div>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.FileUpload {
	position: relative;

	.disabled-item {
		pointer-events: none;
		background-color: #f1f1f1;
		opacity: 0.6;
	}
	
	&.is-focused {
		.inner-container {
			--borderColor: var(--primary-blue-base);
		}
	}

	&.has-validation-error {
		.inner-container {
			--labelColor: var(--semantic-red-base);
			--borderColor: var(--semantic-red-light);
			/* --inputValueColor: var(--semantic-red-base); */
			/* --prefixColor: var(--semantic-red-base); */
			
			&:hover {
				--borderColor: #{var.$semantic-red-base-50-opacity};
			}
			
		}
		.text-label {
			--labelColor: var(--semantic-red-base) !important;
		}
		.short-description-wrapper {
			color: var(--semantic-red-base);
		}
		
		&.is-focused {
			.inner-container {
				--borderColor: var(--semantic-red-base);
			}
		}
	}
	
	&.is-dropzone-active {
		.inner-container {
			
		}
	}
}

.inner-container {
	--borderColor: var(--neutral-grey-light);
	--bgColor: var(--neutral-grey-ultralight);
	--labelColor: var(--neutral-grey-ultradark);
	--placeholderColor: var(--neutral-grey-ultradark);
	--inputValueColor: var(--text-color);
	--prefixColor: var(--primary-blue-base);
	position: relative;
	border-radius: 12px;
	background-color: var(--bgColor);
	border: 2px solid var(--borderColor);
	transition-property: border-color;
	transition-duration: 0.2s;
	
	&.error {
		--borderColor: var(--semantic-red-base);
	}
	
	&.disabled {
		color: var(--neutral-grey-dark);
		fill: var(--neutral-grey-dark);
		opacity: 0.7;
		pointer-events: none;
	}
	
	&:hover {
		--borderColor: var(--neutral-grey-base);
	}
	
	&:focus-within {
		--borderColor: var(--primary-blue-base);
	}

	&.mheHeight {
		min-height: 208px;
	}

	&.hightLight {
		--borderColor: var(--semantic-red-base);
	}
}

.inner-wrapper {
	flex-grow: 1;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	
	.file-upload-input {
		cursor: pointer;
	}
	
	&:hover {
		.upload-button-label {
			@apply underline;
		}
	}
}

.input {
	position: absolute;
	opacity: 0;
	width: 100%;
	height: 100%;
	left: 0;
	top: 0;
}

.dropzone-hint {
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	z-index: 10;
	border-radius: 12px;
	background-color: var(--neutral-grey-dark);
	animation: fade-in 0.35s forwards;
	pointer-events: none;
}

@keyframes fade-in {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}


.attached-file-wrapper {
	
}
.attached-file {
	border-radius: 24px;
	background-color: var(--neutral-grey-extralight);
	display: flex;
	align-items: center;
	
	&.error {
		background-color: var(--semantic-red-extralight);
	}
}

</style>
